import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Package, Truck, X, ChevronRight, User, ShoppingBag, Search } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import API_BASE_URL from '../../config/api';

const FranchiseOrders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [updating, setUpdating] = useState(false);
    const { showAlert } = useAlert();

    const [updateForm, setUpdateForm] = useState({
        status: '',
        tracking_number: '',
        courier_name: ''
    });

    useEffect(() => {
        fetchOrders();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchOrders = async () => {
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/franchise/orders.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setOrders(data.data);
            }
        } catch (error) {
            console.error('Error fetching orders:', error);
            showAlert('Failed to load orders', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateOrder = async (e) => {
        e.preventDefault();
        setUpdating(true);
        try {
            const token = localStorage.getItem('authToken');
            const response = await fetch(`${API_BASE_URL}/franchise/update_order.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id: selectedOrder.id,
                    ...updateForm
                })
            });
            const data = await response.json();
            if (data.success) {
                showAlert('Order updated successfully', 'success');
                setSelectedOrder(null);
                fetchOrders();
            } else {
                showAlert(data.message || 'Update failed', 'error');
            }
        } catch (error) {
            showAlert('Failed to update order', 'error');
        } finally {
            setUpdating(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'Pending': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
            case 'Approved': return 'text-blue-600 bg-blue-50 border-blue-200';
            case 'Ready to Dispatch': return 'text-purple-600 bg-purple-50 border-purple-200';
            case 'Shipped': return 'text-indigo-600 bg-indigo-50 border-indigo-200';
            case 'Delivered': return 'text-green-600 bg-green-50 border-green-200';
            case 'Cancelled': return 'text-red-600 bg-red-50 border-red-200';
            default: return 'text-gray-600 bg-gray-50 border-gray-200';
        }
    };

    const filteredOrders = orders.filter(o =>
        o.order_unique_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        o.shipping_city?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <Layout>
            <div className="p-4 sm:p-6 max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-black text-gray-900 flex items-center gap-3">
                            <ShoppingBag className="text-blue-600" size={32} />
                            Customer Orders
                        </h1>
                        <p className="text-gray-500 font-medium tracking-tight">Manage and fulfill product orders assigned to your franchise</p>
                    </div>

                    <div className="relative w-full md:w-72">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder="Search orders..."
                            className="w-full pl-10 pr-4 py-3 bg-white border border-gray-100 rounded-2xl text-sm focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all shadow-sm"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center p-12">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
                    </div>
                ) : filteredOrders.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {filteredOrders.map(order => (
                            <div key={order.id} className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-xl hover:shadow-gray-100 transition-all duration-300 group">
                                <div className="p-6 border-b border-gray-50 bg-gray-50/50">
                                    <div className="flex justify-between items-start mb-4">
                                        <div className="p-3 bg-white rounded-2xl shadow-sm border border-gray-100 text-blue-600">
                                            <Package size={24} />
                                        </div>
                                        <span className={`px-3 py-1 rounded-xl text-[10px] font-black uppercase tracking-widest border ${getStatusColor(order.status)}`}>
                                            {order.status}
                                        </span>
                                    </div>
                                    <h3 className="font-black text-gray-900">{order.order_unique_id}</h3>
                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
                                        {new Date(order.created_at).toLocaleDateString()} at {new Date(order.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>

                                <div className="p-6">
                                    <div className="space-y-4 mb-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
                                                <User size={14} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Customer</p>
                                                <p className="text-sm font-bold text-gray-900">{order.customer_name}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
                                                <Truck size={14} />
                                            </div>
                                            <div>
                                                <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Shipping Location</p>
                                                <p className="text-sm font-bold text-gray-900 line-clamp-1">{order.shipping_city}, {order.shipping_state}</p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="bg-gray-50 rounded-2xl p-4 mb-6">
                                        <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mb-2">Order Items</p>
                                        <p className="text-sm font-medium text-gray-700 line-clamp-2">{order.items_summary}</p>
                                    </div>

                                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                                        <div className="text-lg font-black text-blue-600">
                                            ₹{parseFloat(order.total_amount).toLocaleString()}
                                        </div>
                                        <button
                                            onClick={() => {
                                                setSelectedOrder(order);
                                                setUpdateForm({
                                                    status: order.status,
                                                    tracking_number: order.tracking_number || '',
                                                    courier_name: order.courier_name || ''
                                                });
                                            }}
                                            className="px-5 py-2.5 bg-gray-900 text-white rounded-xl text-xs font-black uppercase tracking-widest hover:bg-black transition-all flex items-center gap-2"
                                        >
                                            Update Status <ChevronRight size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-20 bg-white rounded-[2.5rem] border border-gray-100 shadow-sm shadow-gray-100/50">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6 text-gray-300">
                            <ShoppingBag size={40} />
                        </div>
                        <h3 className="text-2xl font-black text-gray-900 mb-2">No orders found</h3>
                        <p className="text-gray-500 max-w-sm mx-auto">When customers choose your franchise for fulfillment, their orders will appear here.</p>
                    </div>
                )}

                {/* Update Modal */}
                {selectedOrder && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
                        <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden animate-slideUp border border-white/20">
                            <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/50">
                                <div>
                                    <h3 className="font-black text-2xl text-gray-900 tracking-tight">Update Order</h3>
                                    <p className="text-xs text-gray-400 font-bold uppercase tracking-[0.2em] mt-1">{selectedOrder.order_unique_id}</p>
                                </div>
                                <button
                                    onClick={() => setSelectedOrder(null)}
                                    className="p-3 bg-white hover:bg-gray-100 rounded-2xl transition-all shadow-sm border border-gray-100 text-gray-400"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <form onSubmit={handleUpdateOrder} className="p-8 space-y-6">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5 ml-1">Current Status</label>
                                    <select
                                        className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-50 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none"
                                        value={updateForm.status}
                                        onChange={e => setUpdateForm({ ...updateForm, status: e.target.value })}
                                        required
                                    >
                                        <option value="Pending">Pending</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Ready to Dispatch">Ready to Dispatch</option>
                                        <option value="Shipped">Shipped</option>
                                        <option value="Delivered">Delivered</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>

                                {(updateForm.status === 'Shipped' || updateForm.status === 'Delivered') && (
                                    <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300">
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5 ml-1">Courier Name</label>
                                            <input
                                                type="text"
                                                className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-50 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-300"
                                                placeholder="e.g. BlueDart, Delhivery"
                                                value={updateForm.courier_name}
                                                onChange={e => setUpdateForm({ ...updateForm, courier_name: e.target.value })}
                                                required
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2.5 ml-1">Tracking Number</label>
                                            <input
                                                type="text"
                                                className="w-full px-5 py-4 bg-gray-50 border-2 border-gray-50 rounded-2xl text-sm font-bold focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-300"
                                                placeholder="Enter tracking ID"
                                                value={updateForm.tracking_number}
                                                onChange={e => setUpdateForm({ ...updateForm, tracking_number: e.target.value })}
                                                required
                                            />
                                        </div>
                                    </div>
                                )}

                                <div className="pt-4 flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setSelectedOrder(null)}
                                        className="flex-1 py-4 bg-gray-100 text-gray-600 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-gray-200 transition-all"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={updating}
                                        className={`flex-[2] py-4 bg-blue-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-blue-700 shadow-xl shadow-blue-100 transition-all flex items-center justify-center ${updating ? 'opacity-70 cursor-wait' : ''}`}
                                    >
                                        {updating ? (
                                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                        ) : 'Save Updates'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default FranchiseOrders;
